import { GetLocations } from "@/server-types";
import { create } from "zustand";
import qz, { PrintData } from "qz-tray";
import { t } from "@lingui/core/macro";
import { sentryLog } from "@/utils/sentry-utils";
import { printEscPosOverBluetooth } from "@/utils/pos-printer/bluetooth";
import { DevicePrinterUserError } from "@/utils/pos-printer/printer-errors";
import { printEscPosOnSunmi } from "@/utils/pos-printer/sunmi";
import type { DevicePrintData } from "@/utils/pos-printer";
import { BrowserPrintData, isBrowserPrintData } from "@/utils/pos-printer/a4";
import { printInBrowser } from "@/utils/pos-printer/browser-print";
import { useDevicePrinterStore } from "@/stores/device-printer-store";
import { isNativePlatform } from "@/utils/native";

/**
 * Thrown when a receipt fails to print due to the local print environment —
 * QZ Tray not running, the printer offline / not found, or missing config.
 * These are user-resolvable, not code bugs: they're logged to Sentry at
 * `warning` level (for spike detection) and dropped from the error stream by
 * `beforeSend` in App.tsx, which matches on `name === "PrinterError"`.
 */
export class PrinterError extends Error {
  constructor(message: string, options?: ErrorOptions) {
    super(message, options);
    this.name = "PrinterError";
  }
}

type PrinterType = "http" | "qztray";

export type PrintReceiptType = "receipt" | "UPN";

type PosPrinterState = {
  isPrinting: boolean;
  selectedPrinterType: PrinterType;
  isQzTraySigningSetup: boolean;
  qzTrayClientCert: string | undefined;
  qzTrayClientKey: string | undefined;
};

type PosPrinterActions = {
  setPrinterType: (printerType: PrinterType) => Promise<void>;
  printReceipt: ({
    printData,
    printerConfig,
    shouldThrow,
  }: {
    printData: PrintData | DevicePrintData | BrowserPrintData;
    printerConfig: GetLocations["response"]["locations"][number]["PosPrinter"];
    shouldThrow?: boolean;
  }) => Promise<void>;
  setupQzTray: ({
    qzTrayClientCert,
    qzTrayClientKey,
  }: {
    qzTrayClientCert?: string;
    qzTrayClientKey?: string;
  }) => void;
};

type PosPrinterStore = PosPrinterState & PosPrinterActions;

export const usePosPrinterStore = create<PosPrinterStore>((set, get) => ({
  isPrinting: false,
  selectedPrinterType: "qztray",
  isQzTraySigningSetup: false,
  qzTrayClientCert: undefined,
  qzTrayClientKey: undefined,

  setupQzTray: ({ qzTrayClientCert, qzTrayClientKey }) => {
    // if (get().isQzTraySigningSetup) {
    //   return;
    // }

    const key = qzTrayClientKey || get().qzTrayClientKey;
    const cert = qzTrayClientCert || get().qzTrayClientCert;

    if (!key || !cert) {
      console.error("QZ Tray client certificate or key is not set");
      return;
    }

    qz.security.setCertificatePromise((resolve, reject) => {
      resolve(cert);
    });
    qz.security.setSignatureAlgorithm("SHA512"); // Since 2.1
    qz.security.setSignaturePromise(function (toSign) {
      return async function (resolve, reject) {
        try {
          const { default: rs, hextorstr, stob64 } = await import("jsrsasign");
          const pk = rs.KEYUTIL.getKey(key);
          const sig = new rs.KJUR.crypto.Signature({
            alg: "SHA512withRSA",
          }); // Use "SHA1withRSA" for QZ Tray 2.0 and older
          sig.init(pk);
          sig.updateString(toSign);
          const hex = sig.sign();
          // console.log("DEBUG: \n\n" + stob64(hextorstr(hex)));
          resolve(stob64(hextorstr(hex)));
        } catch (err) {
          console.error(err);
          reject("Signature error: " + err);
        }
      };
    });

    set({
      isQzTraySigningSetup: true,
      qzTrayClientCert,
      qzTrayClientKey,
    });
  },

  setPrinterType: async (printerType: PrinterType) => {
    if (printerType === get().selectedPrinterType) {
      return;
    }

    const previousType = get().selectedPrinterType;

    if (previousType === "qztray") {
      console.log("disconnecting from qztray");
      await qz.websocket.disconnect();
      // set({ isQzTraySigningSetup: false });
    }

    set({ selectedPrinterType: printerType });
  },

  printReceipt: async ({ printData, printerConfig, shouldThrow }) => {
    const { selectedPrinterType } = get();

    // The mobile app prints on the device's own printer — a paired Bluetooth
    // one or the built-in printer of a Sunmi POS device; QZ Tray is web-only.
    if (isNativePlatform()) {
      const devicePrinter = useDevicePrinterStore.getState().selectedPrinter;

      try {
        if (printData.type !== "device-escpos") {
          throw new Error(
            `Unexpected print data type for device printing: ${printData.type}`,
          );
        }

        if (!devicePrinter) {
          throw new DevicePrinterUserError(
            t`Tiskalnik ni izbran. Izberite ga v nastavitvah davčne blagajne.`,
          );
        }

        if (devicePrinter.kind === "sunmi-internal") {
          await printEscPosOnSunmi(printData.bytes);
        } else {
          await printEscPosOverBluetooth(
            devicePrinter.deviceId,
            printData.bytes,
          );
        }
      } catch (error) {
        // Same contract as the QZ Tray branch: user-resolvable print
        // failures are warnings, surfaced to the caller via PrinterError.
        sentryLog.warn("POS receipt print failed", {
          cause: error instanceof Error ? error.message : String(error),
          printerName: devicePrinter?.name,
          selectedPrinterType: devicePrinter?.kind ?? "none",
        });
        if (shouldThrow) {
          throw new PrinterError(
            error instanceof DevicePrinterUserError
              ? error.message
              : t`Prišlo je do napake pri tiskanju računa. Preverite povezavo z tiskalnikom`,
            { cause: error },
          );
        }
      } finally {
        set({ isPrinting: false });
      }
      return;
    }

    // A4 printers print through the browser's dialog; QZ Tray is not needed.
    if (isBrowserPrintData(printData)) {
      try {
        await printInBrowser(printData);
      } catch (error) {
        sentryLog.warn("POS receipt print failed", {
          cause: error instanceof Error ? error.message : String(error),
          selectedPrinterType: "browser",
        });
        if (shouldThrow) {
          throw new PrinterError(
            t`Prišlo je do napake pri tiskanju računa. Preverite povezavo z tiskalnikom`,
            { cause: error },
          );
        }
      } finally {
        set({ isPrinting: false });
      }
      return;
    }

    if (printData.type === "device-escpos") {
      throw new Error("Device print data cannot be printed via QZ Tray");
    }

    console.log("printing with selectedPrinterType", selectedPrinterType);
    if (selectedPrinterType === "qztray") {
      try {
        // const printers = await qz.printers.find();
        if (!printerConfig) {
          sentryLog.warn("POS receipt print failed: missing printer config", {
            reason: "missing_printer_config",
            selectedPrinterType,
          });
          if (shouldThrow) {
            throw new PrinterError("Printer config not provided");
          }
          return;
        }

        if (!qz.websocket.isActive()) {
          console.log("connecting to qztray");
          await qz.websocket.connect({
            host: printerConfig.qzTrayHostIp || undefined,
            retries: 5,
            delay: 1,
          });
        }

        console.log("Found printer:", printerConfig.printerName);
        const config = qz.configs.create(printerConfig.printerName, {
          copies: 1,
          jobName: `Lime Receipt Print`,
          colorType: "blackwhite",
          duplex: false,
          margins: 0,
          rotation: 0,
          scaleContent: false,
        });

        await qz.print(config, [printData]);

        console.log("Receipt printed via QZ Tray");
      } catch (error) {
        // Expected, user-resolvable failures: QZ Tray not running, printer
        // offline or not found. Log at warning for spike detection (the user
        // already sees a toast); beforeSend keeps these out of the error stream.
        sentryLog.warn("POS receipt print failed", {
          cause: error instanceof Error ? error.message : String(error),
          printerName: printerConfig?.printerName,
          qzTrayHostIp: printerConfig?.qzTrayHostIp ?? undefined,
          selectedPrinterType,
        });
        if (shouldThrow) {
          throw new PrinterError(
            t`Prišlo je do napake pri tiskanju računa. Preverite povezavo z tiskalnikom`,
            {
              cause: error,
            },
          );
        }
      }
    } else if (selectedPrinterType === "http") {
      console.error("HTTP NOT SUPPORTED YET");

      // HTTP printing logic (for a network printer with an API)
      // if (!httpPrinterUrl) {
      //   console.error("No HTTP printer URL configured");
      //   return;
      // }

      // try {
      //   const response = await fetch(httpPrinterUrl, {
      //     method: "POST",
      //     headers: {
      //       "Content-Type": "application/json",
      //     },
      //     body: JSON.stringify({
      //       commands, // Send the receipt commands as payload
      //     }),
      //   });

      //   if (!response.ok) {
      //     throw new Error("Failed to send print job over HTTP");
      //   }
      //   console.log("Receipt printed via HTTP");
      // } catch (error) {
      //   console.error("HTTP print error:", error);
      // }
    }

    // Update printing state
    set({ isPrinting: false });
  },
}));
